import React from 'react';
import {API, graphqlOperation} from "aws-amplify";


/**
 * SympatheticErrorPage.jsx
 * A responsive, accessible error page React component using Tailwind CSS.
 * Default export is a React component. Props allow easy customization.
 *
 * Props:
 *  - code (number|string) -> HTTP status code or label (default: 500)
 *  - title (string) -> short headline (default: "Something went wrong")
 *  - description (string) -> supportive message (default provided)
 *  - onRetry (function) -> optional retry callback
 *  - onHome (function) -> optional go-home callback
 *  - showHome (bool) -> whether to show home button (default: true)
 *
 * Tailwind required. If you don't use Tailwind, convert classes to your CSS.
 */

function ErrorPage({
                       resetError,
                       e,
                       code = 500,
                       title = 'Something went wrong',
                       description = "Sorry — we hit a snag. This page tried its best, but needs a little help to continue.",
                       onRetry = null,
                       onHome = null,
                       showHome = true,
                   }) {
    return (
        <div className="h-100 d-row  a-center j-center bg-gray-50 p-6">
            <div className="max-w-3xl w-full bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
                <div className="p-8 md:flex md:items-center">
                    <div className="flex-shrink-0 flex items-center justify-center w-40 h-40 md:w-48 md:h-48">
                        {/* Cute sympathetic face SVG */}
                        <svg
                            viewBox="0 0 120 120"
                            className="w-36 h-36 md:w-44 md:h-44"
                            role="img"
                            aria-labelledby="faceTitle faceDesc"
                        >
                            <title id="faceTitle">Upset sympathetic face</title>
                            <desc id="faceDesc">A cute but upset face that looks worried about the error.</desc>


                            <defs>
                                <linearGradient id="g" x1="0" x2="1">
                                    <stop offset="0%" stopColor="#FEF9C3"/>
                                    <stop offset="100%" stopColor="#FBCFE8"/>
                                </linearGradient>
                            </defs>


                            {/* Head */}
                            <circle cx="60" cy="60" r="54" fill="url(#g)" stroke="#FBCFE8" strokeWidth="1"/>


                            {/* Eyes - tilted brows for worried look */}
                            <circle cx="42" cy="55" r="7" fill="#3B3B3B"/>
                            <circle cx="78" cy="55" r="7" fill="#3B3B3B"/>
                            <path d="M34 46 q8 -6 16 0" stroke="#4B5563" strokeWidth="3" strokeLinecap="round"/>
                            <path d="M70 46 q8 -6 16 0" stroke="#4B5563" strokeWidth="3" strokeLinecap="round"/>


                            {/* Sparkle in eyes */}
                            <circle cx="40" cy="53" r="2" fill="#fff"/>
                            <circle cx="76" cy="53" r="2" fill="#fff"/>


                            {/* Blush (lighter for sadder tone) */}
                            <ellipse cx="35" cy="70" rx="6" ry="3" fill="#FCA5A5" opacity="0.4"/>
                            <ellipse cx="85" cy="70" rx="6" ry="3" fill="#FCA5A5" opacity="0.4"/>


                            {/* Upset mouth - downward curve */}
                            <path
                                d="M44 82q16 -12 32 0"
                                stroke="#4B5563"
                                strokeWidth="3"
                                strokeLinecap="round"
                                fill="transparent"
                                className="face-mouth"
                            />
                        </svg>
                    </div>

                    <div className="mt-6 md:mt-0 md:ml-8 flex-1">
                        <div className="flex items-baseline gap-3">
                            <h1 className="text-2xl font-semibold text-gray-700">{title}</h1>
                        </div>

                        <p className="mt-4 text-gray-600 leading-relaxed">My bad - I've already been alerted</p>

                        <div className="mt-6 flex flex-wrap gap-3">
                            <button onClick={() => {
                                resetError()
                                window.location.reload()
                            }}>
                                Reload
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            <style>{`
.face-mouth {
transform-origin: center;
animation: mouth-quiver 1800ms ease-in-out infinite;
}


@keyframes mouth-quiver {
0% { transform: translateY(0); }
50% { transform: translateY(1px); }
100% { transform: translateY(0); }
}
`}</style>
        </div>
    );
}

export const createAppError = /* GraphQL */`

  mutation CreateAppError(
    $input: CreateAppErrorInput!
    $condition: ModelAppErrorConditionInput
  ) {
    createAppError(input: $input, condition: $condition) {
      id
      location
      userSub
      message
      fileName
      lineNo
      mortgageID
      cleared
      createdAt
      updatedAt
      _version
      _deleted
      _lastChangedAt
      owner
      __typename
    }
  }
`;

export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false, error: null};
    }

    static getDerivedStateFromError(error) {
        return {hasError: true, error};
    }

    parseErrorStack(error) {
        // Fallback parsing if fileName / lineNumber not set
        const stackLines = (error.stack || "").split("\n");
        if (stackLines.length > 1) {
            // Usually line 1-2 contains file info: at func (file:line:col)
            const match = stackLines[1].match(/\(?(.+):(\d+):(\d+)\)?$/);
            if (match) {
                return { fileName: match[1], lineNo: parseInt(match[2], 10) , stack: error.stack };
            }
        }
        return { fileName: null, lineNo: null , stack: error.stack };
    }

    createErrorRecord = async (error) => {
        // Deduplicate in dev to avoid React StrictMode double calls
        if (process.env.NODE_ENV === "development") {
            return
            if (!window.__reportedErrors) window.__reportedErrors = new Set();
            const key = error.stack || error.message;
            if (window.__reportedErrors.has(key)) return;
            window.__reportedErrors.add(key);
        }

        const { fileName, lineNo, stack } = this.parseErrorStack(error);

        const input = {
            location: window.location.href,
            message: error.message,
            fileName: error.fileName || fileName || null,
            lineNo: error.lineNumber || lineNo || null,
            stack
        };

        try {
            await API.graphql(graphqlOperation(createAppError, { input }));
        } catch (graphqlError) {
            console.error("Failed to save error to backend:", graphqlError);
        }
    };

    componentDidCatch(error, errorInfo) {
        this.createErrorRecord(error).catch(console.error);
    }

    resetError = () => {
        this.setState({hasError: false, error: null});
    }

    render() {
        if (this.state.hasError) {
            return (
                <ErrorPage
                    resetError={this.resetError}
                    e={this.state.error}
                    code={500}
                    title="Something went wrong"
                    description={this.state.error?.message || "An unexpected error occurred."}
                    onRetry={() => window.location.reload()}
                />
            );
        }
        return this.props.children;
    }
}

export default ErrorPage;